import React from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Popover,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckIcon from '@mui/icons-material/Check';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import { AppContext, SnackContext, UserContext } from 'src/store/ContextStore';
import { APICONSTANTS, CONSTANTS } from 'src/components/Constants';
import axios from 'src/axios';
import { isAnonymousOrLoading } from '../../anonymousPedigrees';
import { LABEL_KEYS } from '../../pedigree-tool/pedigree/labelDefinitions';
import {
  buildSchemaDefaults,
  resolveLabelFormatting,
  foldResolvedIntoLabelsSet
} from '../../pedigree-tool/pedigree/resolveLabelFormatting';

const TEAL = '#1a7a8a';
const TEAL_BG = 'rgba(43, 169, 191, 0.08)';
const TEAL_BORDER = 'rgba(43, 169, 191, 0.5)';

// Scope → display chip. Most-specific (user) is the warmest accent.
const SCOPE_PILL = {
  system: { label: 'System', color: '#6b7280' },
  group: { label: 'Group', color: '#2BA9BF' },
  user: { label: 'User', color: '#7c5cbf' }
};

const pickDefault = (arr) => (arr || []).find((p) => p.isDefault) || null;

// The `settings` blob a saved preset carries, alongside its per-label `overrides`.
//
// Extracted from the save handler so the SHAPE is assertable (TGCLOUD-1630): the KB generator's
// grounding notes state which chart-wide options a preset does and does not store, and that claim had
// already gone stale once. A test now pins these keys against the note rather than a human
// remembering to update prose when the object changes.
//
// `settings` is spread wholesale into `labelsSet` on load, which is why a new chart-wide switch needs
// no schema change and no resolve-path change to become preset-backed.
//
// Deliberately NOT carrying the other chart-wide switches (showCarrierMarks, showAnalysisOverlay,
// standardNomenclature, showRecordStatus): those are per-session view state, not part of a saved
// label configuration.
export const PRESET_SETTINGS_KEYS = [
  'displayLabelMode',
  'carrierMarkStyle',
  'probandOnlyLabels'
];

export const presetSettingsFrom = (labelsSet, displayLabelMode) => ({
  displayLabelMode,
  carrierMarkStyle: labelsSet?.carrierMarkStyle || 'border',
  // TGCLOUD-1465 — the de-identified copy is meant to be REUSABLE ("save it as a reusable display
  // preset"), and at System/Group scope this lets a site make it the default.
  probandOnlyLabels: !!labelsSet?.probandOnlyLabels
});

// Section overline label — small uppercase grey heading.
//
// `...rest` is what carries `data-va-anchor` onto a real DOM node. Without it the attribute was
// swallowed here, so `pedigree.labels.displayMode` never existed in the document and the Copilot
// walkthrough that steps onto it spotlighted nothing (found while writing the TGCLOUD-1666 tests —
// the sibling `pedigree.labels.presetName` anchor works because it sits on a TextField, which
// forwards unknown props by default).
const SectionLabel = ({ children, after, ...rest }) => (
  <Box
    {...rest}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      mb: 1
    }}
  >
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: 'text.disabled'
      }}
    >
      {children}
    </Typography>
    {after}
  </Box>
);

// Two-line radio label: title + grey sublabel
const TwoLineLabel = ({ title, sub }) => (
  <Box>
    <Typography sx={{ fontSize: 14, lineHeight: 1.25 }}>{title}</Typography>
    <Typography
      sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.25 }}
    >
      {sub}
    </Typography>
  </Box>
);

// Preview card for a display label mode.
//
// TGCLOUD-1666: these cards used to hardcode `F-12345` / `G-8-00014`, which is what produced the
// report that "the numbers do not match anything" — a mockup reads as a preview, so the user could
// not tell which identifier the mode would actually surface. They now carry THIS family's real
// G number and real primary external ID, mirroring exactly what the chart header draws.
const DisplayLabelPreview = ({ mode, active, gNumber, externalId }) => {
  const isExternal = mode === 'primary-external';
  return (
    <Box
      sx={{
        position: 'relative',
        flex: 1,
        minWidth: 0,
        borderRadius: 1,
        border: '1px solid',
        borderColor: active ? TEAL_BORDER : 'divider',
        backgroundColor: active ? TEAL_BG : '#fafafa',
        p: 1.25,
        transition: 'background-color 120ms, border-color 120ms'
      }}
    >
      {active && (
        <CheckIcon
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            fontSize: 14,
            color: TEAL
          }}
        />
      )}
      <Typography
        sx={{
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'text.disabled'
        }}
      >
        {isExternal ? 'External ID' : 'G number'}
      </Typography>
      {/* A site's own identifier can be long, and both lines are nowrap+ellipsis — so each carries
          a `title`, the same affordance the chart header gives its headline and chips. */}
      <Typography
        title={isExternal ? externalId : gNumber}
        sx={{
          fontSize: 13,
          fontWeight: 700,
          color: TEAL,
          mt: 0.25,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
      >
        {isExternal ? externalId : gNumber}
      </Typography>
      <Typography
        title={isExternal ? gNumber : externalId}
        sx={{
          fontSize: 10,
          color: 'text.secondary',
          mt: 0.25,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
      >
        {isExternal ? `${gNumber} · Master ID` : `AKA ${externalId}`}
      </Typography>
    </Box>
  );
};

export default function LabelConfigurations({ labelsSet, setLabelsSet }) {
  const { setSnack } = React.useContext(SnackContext);
  const { content } = React.useContext(AppContext);
  const { user } = React.useContext(UserContext);
  const anonymous = isAnonymousOrLoading(content?.settings?.pedigreeSetting);

  // The family's own identifiers, read from the SAME context slot the chart header reads
  // (`PedigreeChart` → `user.familyId` / `user.externalFamilyIds`) rather than arriving as a
  // `hasExternalIds` boolean prop. Two consequences, both wanted: the section cannot disagree with
  // the sidebar about whether an external ID exists, and the preview cards can show the real values
  // instead of placeholders (TGCLOUD-1666).
  const familyRoot = content?.pedigreeSelectedUsers?.selectedUser;
  const gNumber = familyRoot?.familyId || '';
  const externalFamilyIds = Array.isArray(familyRoot?.externalFamilyIds)
    ? familyRoot.externalFamilyIds
    : [];
  // The primary alias is the FIRST entry — "primary" means first in the list, which is what the
  // starred chip in EXTERNAL IDS sets. Multiple IDs are out of scope (TGCLOUD-1666): the mode
  // surfaces the primary, and the rest stay visible as "Also known as" chips on the chart.
  const primaryExternalId = externalFamilyIds[0] || '';
  const hasExternalIds = externalFamilyIds.length > 0;

  // Scopes the current user may manage (save / set-default / delete). Everyone
  // owns their `user` presets; group presets are managed by Genetics Service,
  // Genetics Service Admin, and System Admin; system presets by System Admin
  // only (and can never be deleted). The server enforces all of this too.
  const userType = user?.userType;
  const canEditGroup =
    userType === CONSTANTS.userTypes.ADMIN ||
    userType === CONSTANTS.userTypes.GENETICS_SERVICE_ADMIN ||
    userType === CONSTANTS.userTypes.GENETICS;
  const canEditSystem = userType === CONSTANTS.userTypes.ADMIN;
  const writableScopes = [
    'user',
    ...(canEditGroup ? ['group'] : []),
    ...(canEditSystem ? ['system'] : [])
  ];
  const canManageScope = (scope) =>
    scope === 'user' ? true : scope === 'group' ? canEditGroup : canEditSystem;
  // Set-default is allowed wherever the caller can manage the scope. Delete is
  // the same EXCEPT system presets, which nobody may delete.
  const canSetDefaultPreset = (p) => canManageScope(p.scope);
  const canDeletePreset = (p) =>
    p.scope !== 'system' && canManageScope(p.scope);

  const [manageAnchor, setManageAnchor] = React.useState(null);

  // Anonymous pedigrees: external family IDs are removed, so any stored
  // 'primary-external' mode is treated as 'g-number'.
  const displayLabelMode = anonymous
    ? 'g-number'
    : labelsSet?.displayLabelMode || 'g-number';

  // ── Scoped presets (system → group → user) ────────────────────────────────
  const [scoped, setScoped] = React.useState({
    system: [],
    group: [],
    user: []
  });
  const [saveScope, setSaveScope] = React.useState('user');
  const [presetName, setPresetName] = React.useState('');
  const [activePresetId, setActivePresetId] = React.useState('');

  // The inherited base BELOW a target scope, used to compute sparse overrides on
  // save and to fall back on reset.
  const baseFor = (scope) =>
    resolveLabelFormatting(
      buildSchemaDefaults(),
      scope === 'system' ? null : pickDefault(scoped.system),
      scope === 'system' || scope === 'group'
        ? null
        : pickDefault(scoped.group),
      null
    );

  // Resolve the three layers and fold into the live working state, carrying the
  // most-specific preset's settings (display mode etc.).
  const applyResolved = (sys, grp, usr) =>
    setLabelsSet((prev) => {
      const resolved = resolveLabelFormatting(
        buildSchemaDefaults(),
        sys,
        grp,
        usr
      );
      const folded = foldResolvedIntoLabelsSet(resolved, prev);
      const top = usr || grp || sys;
      return { ...folded, ...(top?.settings || {}) };
    });

  // Fetch scoped presets + resolve the initial working state once on mount.
  const scopedAppliedRef = React.useRef(false);
  React.useEffect(() => {
    axios
      .get(APICONSTANTS.labelDisplayPresets)
      .then(({ data }) => {
        const next = {
          system: data?.system || [],
          group: data?.group || [],
          user: data?.user || []
        };
        setScoped(next);
        if (!scopedAppliedRef.current) {
          scopedAppliedRef.current = true;
          const sys = pickDefault(next.system);
          const grp = pickDefault(next.group);
          const usr = pickDefault(next.user);
          applyResolved(sys, grp, usr);
          const def = usr || grp || sys;
          if (def?._id) setActivePresetId(def._id);
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const allScopedPresets = [
    ...scoped.system.map((p) => ({ ...p, scope: 'system' })),
    ...scoped.group.map((p) => ({ ...p, scope: 'group' })),
    ...scoped.user.map((p) => ({ ...p, scope: 'user' }))
  ];

  const handleScopedSelect = (e) => {
    const id = e.target.value;
    const preset = allScopedPresets.find((p) => p._id === id);
    if (!preset) return;
    setActivePresetId(id);
    // Layer the chosen preset on top of the inherited system←group base, so its
    // labels win and unspecified labels still inherit.
    applyResolved(
      pickDefault(scoped.system),
      pickDefault(scoped.group),
      preset
    );
  };

  const handleResetInherited = () => {
    // Fall back a layer: drop the user override from the working view.
    setActivePresetId('');
    applyResolved(pickDefault(scoped.system), pickDefault(scoped.group), null);
    setSnack({
      status: true,
      msg: 'Reset to inherited formatting',
      severity: 'info'
    });
  };

  const handleDisplayLabelModeChange = (e) => {
    const next = e.target.value;
    setLabelsSet((prev) => ({ ...prev, displayLabelMode: next }));
  };

  const handleScopedSave = () => {
    const fallbackName = allScopedPresets.find(
      (p) => p._id === activePresetId
    )?.name;
    const name = presetName.trim() || fallbackName;
    if (!name) {
      setSnack({
        status: true,
        msg: 'Enter a preset name',
        severity: 'warning'
      });
      return;
    }
    // Capture a SPARSE override: only labels that deviate from the inherited
    // base for the target scope, so a preset overrides exactly what it specifies.
    const base = baseFor(saveScope);
    const overrides = {};
    for (const key of LABEL_KEYS) {
      const visible = Boolean(labelsSet?.[key]);
      const f = labelsSet?.formatting?.[key] || {};
      const bold = !!f.bold;
      const italic = !!f.italic;
      const b = base[key] || { visible: false, bold: false, italic: false };
      const ov = {};
      if (b.visible !== visible) ov.visible = visible;
      if (b.bold !== bold) ov.bold = bold;
      if (b.italic !== italic) ov.italic = italic;
      if (Object.keys(ov).length) overrides[key] = ov;
    }
    axios
      .post(APICONSTANTS.labelDisplayPresets, {
        scope: saveScope,
        name,
        isDefault: true,
        overrides,
        settings: presetSettingsFrom(labelsSet, displayLabelMode)
      })
      .then(({ data }) => {
        setPresetName('');
        if (data?._id) setActivePresetId(data._id);
        return axios.get(APICONSTANTS.labelDisplayPresets);
      })
      .then((res) => {
        if (res?.data) {
          setScoped({
            system: res.data.system || [],
            group: res.data.group || [],
            user: res.data.user || []
          });
        }
        setSnack({ status: true, msg: 'Preset saved', severity: 'success' });
      })
      .catch((err) => {
        const status = err?.response?.status;
        setSnack({
          status: true,
          msg:
            status === 403
              ? 'You do not have permission to save at that scope'
              : 'Could not save preset',
          severity: 'error'
        });
      });
  };

  const refetchPresets = () =>
    axios.get(APICONSTANTS.labelDisplayPresets).then((res) => {
      const next = {
        system: res?.data?.system || [],
        group: res?.data?.group || [],
        user: res?.data?.user || []
      };
      setScoped(next);
      return next;
    });

  const handleSetDefault = (p) => {
    axios
      .post(`${APICONSTANTS.labelDisplayPresets}/${p._id}/default`)
      .then(() => refetchPresets())
      .then(() =>
        setSnack({
          status: true,
          msg: `"${p.name}" is now the ${SCOPE_PILL[
            p.scope
          ].label.toLowerCase()} default`,
          severity: 'success'
        })
      )
      .catch((err) =>
        setSnack({
          status: true,
          msg:
            err?.response?.status === 403
              ? 'You do not have permission to set that default'
              : 'Could not update the default',
          severity: 'error'
        })
      );
  };

  const handleDeletePreset = (p) => {
    axios
      .delete(`${APICONSTANTS.labelDisplayPresets}/${p._id}`)
      .then(() => refetchPresets())
      .then((next) => {
        // If the applied preset was deleted, fall back to the inherited view.
        if (activePresetId === p._id) {
          setActivePresetId('');
          applyResolved(
            pickDefault(next.system),
            pickDefault(next.group),
            pickDefault(next.user)
          );
        }
        setSnack({ status: true, msg: 'Preset deleted', severity: 'success' });
      })
      .catch((err) =>
        setSnack({
          status: true,
          msg:
            err?.response?.status === 403
              ? 'You do not have permission to delete that preset'
              : 'Could not delete preset',
          severity: 'error'
        })
      );
  };

  return (
    <Box sx={{ border: '1px solid rgba(0,0,0,.125)', borderBottom: 0 }}>
      {/* Section header */}
      <Box
        sx={{
          backgroundColor: 'rgba(0,0,0,.03)',
          borderBottom: '1px solid rgba(0,0,0,.125)',
          px: 2,
          minHeight: 56,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}
      >
        <IconButton color="primary">
          <SettingsIcon fontSize="medium" />
        </IconButton>
        <Typography
          sx={{
            pt: 0.1
          }}
        >
          CONFIGURATIONS
        </Typography>
      </Box>
      {/* Section body */}
      <Box sx={{ p: 2 }}>
        {/* ── PRESET (scoped visibility + formatting) ───────────────────── */}
        <SectionLabel>Preset</SectionLabel>
        <TextField
          select
          size="small"
          fullWidth
          value={activePresetId}
          onChange={handleScopedSelect}
          slotProps={{
            htmlInput: { style: { fontSize: '0.875rem' } },
            select: { displayEmpty: true }
          }}
        >
          {allScopedPresets.length === 0 && (
            <MenuItem value="" disabled>
              No presets available
            </MenuItem>
          )}
          {allScopedPresets.map((p) => (
            <MenuItem key={p._id} value={p._id}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  width: '100%'
                }}
              >
                <Box component="span" sx={{ flex: 1 }}>
                  {p.name}
                  {p.isDefault ? ' (default)' : ''}
                </Box>
                <Chip
                  label={SCOPE_PILL[p.scope].label}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '0.62rem',
                    fontWeight: 700,
                    color: '#fff',
                    backgroundColor: SCOPE_PILL[p.scope].color,
                    '& .MuiChip-label': { px: 0.75 }
                  }}
                />
              </Box>
            </MenuItem>
          ))}
        </TextField>

        {/* Save row: scope (when more than one is writable) + name + Save */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1.5 }}>
          {writableScopes.length > 1 && (
            <TextField
              select
              size="small"
              value={saveScope}
              onChange={(e) => setSaveScope(e.target.value)}
              sx={{ width: 104 }}
              slotProps={{
                htmlInput: { style: { fontSize: '0.8rem' } }
              }}
            >
              {writableScopes.map((s) => (
                <MenuItem key={s} value={s}>
                  {SCOPE_PILL[s].label}
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            size="small"
            placeholder="Preset name..."
            data-va-anchor="pedigree.labels.presetName"
            value={presetName}
            onChange={(e) => setPresetName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleScopedSave();
            }}
            sx={{ flex: 1 }}
            slotProps={{
              htmlInput: { style: { fontSize: '0.875rem' } }
            }}
          />
          <Button variant="contained" size="small" onClick={handleScopedSave}>
            Save preset
          </Button>
        </Box>

        {/* Reset (fall back a layer) + Manage (set-default / delete) */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
          <Button
            variant="text"
            size="small"
            startIcon={<RestartAltIcon fontSize="small" />}
            onClick={handleResetInherited}
            sx={{ px: 0, fontSize: '0.75rem', textTransform: 'none' }}
          >
            Reset to inherited
          </Button>
          <Button
            variant="text"
            size="small"
            onClick={(e) => setManageAnchor(e.currentTarget)}
            sx={{ px: 0, fontSize: '0.75rem', textTransform: 'none' }}
          >
            Manage presets
          </Button>
        </Box>

        {/* Manage popover — set default (star) per scope + delete (role-gated) */}
        <Popover
          open={Boolean(manageAnchor)}
          anchorEl={manageAnchor}
          onClose={() => setManageAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Box sx={{ p: 1.5, minWidth: 320 }}>
            <Typography
              variant="subtitle2"
              sx={{ mb: 1, fontWeight: 700, fontSize: '0.8rem' }}
            >
              Manage presets
            </Typography>
            {allScopedPresets.map((p) => (
              <Box
                key={p._id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  py: 0.5,
                  borderBottom: '1px solid rgba(0,0,0,.08)'
                }}
              >
                <Tooltip
                  arrow
                  title={
                    p.isDefault
                      ? 'Default'
                      : canSetDefaultPreset(p)
                      ? 'Set as default'
                      : ''
                  }
                >
                  <span>
                    <IconButton
                      size="small"
                      disabled={p.isDefault || !canSetDefaultPreset(p)}
                      onClick={() => handleSetDefault(p)}
                    >
                      {p.isDefault ? (
                        <StarIcon fontSize="small" sx={{ color: '#f5a623' }} />
                      ) : (
                        <StarBorderIcon fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
                <Typography sx={{ flex: 1, fontSize: '0.875rem', minWidth: 0 }}>
                  {p.name}
                </Typography>
                <Chip
                  label={SCOPE_PILL[p.scope].label}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '0.62rem',
                    fontWeight: 700,
                    color: '#fff',
                    backgroundColor: SCOPE_PILL[p.scope].color,
                    '& .MuiChip-label': { px: 0.75 }
                  }}
                />
                {canDeletePreset(p) ? (
                  <Tooltip title="Delete preset" arrow>
                    <IconButton
                      size="small"
                      onClick={() => handleDeletePreset(p)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : (
                  // keep rows aligned where delete isn't permitted (system)
                  <Box sx={{ width: 30, flexShrink: 0 }} />
                )}
              </Box>
            ))}
          </Box>
        </Popover>

        {/* ── DISPLAY LABEL ────────────────────────────────────────────── */}
        {/*
          Shown ONLY when there is a real choice to make (TGCLOUD-1666). The section used to render
          unconditionally, with the external option disabled behind a "add an External Family ID to
          enable this option" tooltip — a configuration panel that could not be configured, which is
          why it read as broken. Two ways it is not a choice:

            • the family has no external ID — there is nothing to surface;
            • anonymous pedigrees — the mode is clamped to the G number, so the radio group
              degenerated to a single option and one preview card.

          The Divider lives inside the conditional too, or the panel ends on a dangling rule.

          Hiding the control does NOT reset `labelsSet.displayLabelMode`: a preset or the usergroup
          default may legitimately carry 'primary-external', the chart already falls back to the
          G number while no external ID exists, and `presetSettingsFrom` below must keep round-
          tripping whatever was inherited.
        */}
        {!anonymous && hasExternalIds && (
          <>
            <Divider sx={{ my: 2 }} />

            <SectionLabel data-va-anchor="pedigree.labels.displayMode">
              Display label
            </SectionLabel>

            <RadioGroup
              value={displayLabelMode}
              onChange={handleDisplayLabelModeChange}
            >
              <FormControlLabel
                value="g-number"
                control={<Radio size="small" />}
                label={
                  <TwoLineLabel
                    title="G number (master ID)"
                    sub="G number shown as primary"
                  />
                }
                sx={{
                  mx: 0,
                  my: 0,
                  alignItems: 'flex-start',
                  '& .MuiFormControlLabel-label': { mt: 0.25 }
                }}
              />
              <FormControlLabel
                value="primary-external"
                control={<Radio size="small" />}
                label={
                  <TwoLineLabel
                    title="Primary external ID"
                    sub="External alias shown as primary"
                  />
                }
                sx={{
                  mx: 0,
                  my: 0,
                  alignItems: 'flex-start',
                  '& .MuiFormControlLabel-label': { mt: 0.25 }
                }}
              />
            </RadioGroup>

            {/* Preview cards — this family's actual identifiers, not placeholders */}
            <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
              <DisplayLabelPreview
                mode="g-number"
                active={displayLabelMode === 'g-number'}
                gNumber={gNumber}
                externalId={primaryExternalId}
              />
              <DisplayLabelPreview
                mode="primary-external"
                active={displayLabelMode === 'primary-external'}
                gNumber={gNumber}
                externalId={primaryExternalId}
              />
            </Box>

            <Typography
              sx={{
                mt: 1.25,
                fontSize: 11,
                color: 'text.secondary',
                lineHeight: 1.4
              }}
            >
              The primary external ID is the starred entry under EXTERNAL IDS.
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
}
